import { useEffect, useRef } from 'react';
import { usePlayer } from '../../context/PlayerContext';

export function LogoEmbers() {
  const canvasRef = useRef(null);
  const { analyserRef, isPlaying } = usePlayer();
  const visibleRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
    }, { threshold: 0.1 });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const mob = window.innerWidth < 1024;
    const ctx = canvas.getContext('2d', { alpha: true });
    let animId;
    let embers = [];

    const W = 420;
    const H = 420;
    canvas.width = W;
    canvas.height = H;

    const cx = W / 2;
    const cy = H / 2;
    const dataArray = new Uint8Array(64);

    const COLORS = [
      [255, 110, 15],
      [218, 165, 32],
      [180, 20, 20],
    ];

    const getEnergy = () => {
      const analyser = analyserRef?.current;
      if (!analyser) return 0;
      try {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < 32; i++) sum += dataArray[i];
        return sum / (32 * 255);
      } catch { return 0; }
    };

    const spawnEmber = () => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 60 + Math.random() * 80;
      return {
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        size: Math.random() * 2.5 + 0.8,
        speedX: (Math.random() - 0.5) * 0.6,
        speedY: -(Math.random() * 1.5 + 0.5),
        life: 1,
        decay: Math.random() * 0.01 + 0.005,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: Math.random() * 0.04 + 0.015,
        color: Math.floor(Math.random() * 3)
      };
    };

    const logoMax = mob ? 10 : 25;
    for (let i = 0; i < (mob ? 8 : 18); i++) {
      const e = spawnEmber();
      e.life = Math.random();
      embers.push(e);
    }

    let frame = 0;
    const animate = () => {
      frame++;
      if (frame % 2 !== 0) { animId = requestAnimationFrame(animate); return; }
      if (!visibleRef.current) { animId = requestAnimationFrame(animate); return; }

      ctx.clearRect(0, 0, W, H);

      const overall = getEnergy();
      const brightness = Math.min(1, overall * 2.5);

      if (frame % (mob ? 10 : 6) === 0 && embers.length < logoMax) embers.push(spawnEmber());

      for (let i = embers.length - 1; i >= 0; i--) {
        const e = embers[i];
        e.wobble += e.wobbleSpeed;
        e.x += e.speedX + Math.sin(e.wobble) * 0.4;
        e.y += e.speedY;
        e.life -= e.decay;
        if (e.life <= 0 || e.y < -10) { embers.splice(i, 1); continue; }

        const [r, g, b] = COLORS[e.color];
        const alpha = e.life * (0.4 + brightness * 0.6);

        ctx.beginPath();
        ctx.arc(e.x, e.y, e.size * e.life, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animId);
  }, [analyserRef, isPlaying]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-0"
      style={{ width: '100%', height: '100%' }}
    />
  );
}
